import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { MAILBOX_FEATURE_SET } from '@stitch/pbx-adapter/catalog';
import { lookup } from '@stitch/pbx-adapter/describe';
import {
  PHONE_MODELS,
  defaultKeys,
  describeModel,
  formatKeys,
  formatMac,
  keyName,
  keyOrder,
  keyText,
  keysChange,
  lineKeyCount,
  looksYealink,
  normalizeMac,
  parseKeys,
  phoneModel,
  type PhoneKey,
  type PhoneModel
} from '@stitch/pbx-adapter/phones';
import { api, type Account, type Directory, type Intent, type PhoneTemplate, type TemplateUse } from '../../api/client';
import { KeyEditor } from '../../components/KeyEditor';
import { PhoneLayout } from '../../components/PhoneLayout';
import { DestinationPicker } from '../../components/pickers';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

/** "Yealink T54W", or what the phone system calls a model Stitch doesn't know. */
export function modelLabel(id: string | undefined): string {
  const m = id ? phoneModel(id) : undefined;
  if (m) return `${m.vendor} ${m.model}`;
  return id?.startsWith('other:') ? id.slice(6) : 'Unknown model';
}

/** A name for a number on a key: people, queues, park orbits, voicemail boxes. */
export const namer = (dir: Directory | undefined) => (n: string) => {
  const info = lookup(dir, n);
  return info.kind === 'unknown' || info.kind === 'external' ? undefined : info.name;
};

const personName = (dir: Directory | undefined, ext: string) => (ext ? lookup(dir, ext).name : '');

/** People who can have a phone: everyone but voicemail boxes. */
const peopleOnly = (dir: Directory | undefined): Directory | undefined =>
  dir ? { ...dir, entries: dir.entries.filter((e) => e.kind === 'person' && e.featureSet !== MAILBOX_FEATURE_SET) } : dir;

function OnlineChip({ a }: { a: Account }) {
  const p = a.live?.phone;
  if (!p) return <span className="muted">–</span>;
  return p.online ? (
    <Chip small tone="ok">
      Online
    </Chip>
  ) : (
    <Chip small>Offline</Chip>
  );
}

// ---- The list ---------------------------------------------------------------------------------

interface NewPhone {
  mac: string;
  model: string;
  user: string;
  template: string;
}
type Staged = { op: 'add'; phone: NewPhone; keys: string } | { op: 'remove'; mac: string; label: string };

export function TenantPhones() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const phones = useData(() => api.accounts(d, 'phone'), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const templates = useData(() => api.templates(d).catch(() => ({ templates: [] as PhoneTemplate[], uses: [] as TemplateUse[] })), [d]);
  const [staged, setStaged] = useState<Staged[]>([]);
  const [adding, setAdding] = useState(false);
  const [addKey, setAddKey] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [q, setQ] = useState('');
  const view = phones.data;
  const writable = Boolean(view?.writable);
  const list = view?.accounts ?? [];
  const useOf = (mac: string) => templates.data?.uses.find((u) => u.phone === mac);
  const tplName = (id: string) => templates.data?.templates.find((t) => t.id === id)?.name ?? 'a template';
  const adds = staged.filter((s): s is Extract<Staged, { op: 'add' }> => s.op === 'add');
  const removing = new Set(staged.filter((s) => s.op === 'remove').map((s) => (s as { mac: string }).mac));
  const base = `/t/${encodeURIComponent(d)}`;

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((a) =>
      [a.extension, formatMac(a.extension), modelLabel(a.values.model), personName(dir.data, a.values.user ?? ''), a.values.user ?? ''].some((v) =>
        v.toLowerCase().includes(needle)
      )
    );
  }, [list, q, dir.data]);

  const online = list.filter((a) => a.live?.phone?.online).length;

  return (
    <>
      <PageHeader
        eyebrow={view ? `${plural(list.length, 'phone')} · ${online} online` : d}
        title="Desk phones"
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search phones</span>
              <input type="search" placeholder="Search person, model or MAC" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Button
              variant="primary"
              icon="plus"
              disabled={!writable}
              onClick={() => {
                setAddKey((k) => k + 1);
                setAdding(true);
              }}
            >
              Add phones
            </Button>
          </>
        }
      />
      <div className="content">
        {view?.notice && (
          <Notice tone="warn" icon="lock" title={view.writableFields.includes('keys') ? 'Keys only for now' : 'View only for now'}>
            {view.notice}
          </Notice>
        )}
        <Card>
          {phones.error ? (
            <LoadError error={phones.error} retry={phones.reload} />
          ) : !view ? (
            <Loading label="Loading phones" />
          ) : list.length === 0 && adds.length === 0 ? (
            <Empty title="No desk phones yet">Add a phone by its MAC address (on the label under the phone) and choose whose it is.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Phone</th>
                    <th scope="col">Whose</th>
                    <th scope="col" className="hide-sm">
                      Status
                    </th>
                    <th scope="col" className="hide-sm">
                      Keys
                    </th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adds.map((a) => (
                    <tr key={`add-${a.phone.mac}`} className="staged">
                      <td>
                        <span className="cell-stack">
                          <span>
                            {modelLabel(a.phone.model)}{' '}
                            <Chip small tone="brand">
                              New
                            </Chip>
                          </span>
                          <small className="mono muted">{formatMac(a.phone.mac)}</small>
                        </span>
                      </td>
                      <td>{a.phone.user ? personName(dir.data, a.phone.user) : <span className="muted">Spare</span>}</td>
                      <td className="hide-sm">–</td>
                      <td className="hide-sm">{a.phone.template ? tplName(a.phone.template) : a.phone.user ? 'Their line' : '–'}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Cancel adding ${formatMac(a.phone.mac)}`}
                            title="Cancel"
                            onClick={() => setStaged((s) => s.filter((x) => x !== a))}
                          >
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.map((a) => {
                    const use = useOf(a.extension);
                    const gone = removing.has(a.extension);
                    const owner = a.values.user ?? '';
                    return (
                      <tr key={a.extension} className={gone ? 'staged removed' : ''}>
                        <td>
                          <Link to={`${base}/phones/${a.extension}`} className="cell-stack plain-link">
                            <span>
                              {modelLabel(a.values.model)}{' '}
                              {gone && (
                                <Chip small tone="crit">
                                  Removing
                                </Chip>
                              )}
                            </span>
                            <small className="mono muted">{formatMac(a.extension)}</small>
                          </Link>
                        </td>
                        <td>
                          {owner ? (
                            <span className="cell-stack">
                              <span>{personName(dir.data, owner)}</span>
                              <small className="muted">Ext {owner}</small>
                            </span>
                          ) : (
                            <span className="muted">Spare</span>
                          )}
                        </td>
                        <td className="hide-sm">
                          <OnlineChip a={a} />
                        </td>
                        <td className="hide-sm">
                          {use ? (
                            <span className="cell-stack">
                              <span>{tplName(use.templateId)}</span>
                              {!use.current && (
                                <Chip small tone="warn">
                                  Template changed since
                                </Chip>
                              )}
                            </span>
                          ) : (
                            <span className="muted">{owner ? 'Own layout' : '–'}</span>
                          )}
                        </td>
                        <td>
                          <div className="row-actions">
                            {gone ? (
                              <Button size="small" variant="ghost" onClick={() => setStaged((s) => s.filter((x) => !(x.op === 'remove' && x.mac === a.extension)))}>
                                Keep it
                              </Button>
                            ) : (
                              <>
                                <Link
                                  className="icon-btn"
                                  to={`${base}/phones/${a.extension}`}
                                  aria-label={`Keys and settings of ${modelLabel(a.values.model)} ${formatMac(a.extension)}`}
                                  title="Keys and settings"
                                >
                                  <Icon name="edit" size={16} />
                                </Link>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Remove ${modelLabel(a.values.model)} ${formatMac(a.extension)}`}
                                  title={view.removable ? 'Remove' : 'Removing phones isn’t switched on yet'}
                                  disabled={!view.removable}
                                  onClick={() => setStaged((s) => [...s, { op: 'remove', mac: a.extension, label: `${modelLabel(a.values.model)} ${formatMac(a.extension)}` }])}
                                >
                                  <Icon name="trash" size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {staged.length > 0 && !reviewing && (
        <div className="tray" role="region" aria-label="Changes to review">
          <div className="grow">
            <strong>{plural(staged.length, 'change')} ready to review</strong>
            <span>Nothing has changed on the phone system yet.</span>
          </div>
          <Button variant="ghost" onClick={() => setStaged([])}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setReviewKey((k) => k + 1);
              setReviewing(true);
            }}
          >
            Review changes
          </Button>
        </div>
      )}

      <AddPhones
        key={addKey}
        open={adding}
        dir={dir.data}
        templates={templates.data?.templates ?? []}
        taken={(mac) => list.some((a) => a.extension === mac) || adds.some((a) => a.phone.mac === mac)}
        onClose={() => setAdding(false)}
        onAdd={(rows) => {
          setStaged((s) => [
            ...s,
            ...rows.map((p): Staged => {
              const t = templates.data?.templates.find((x) => x.id === p.template);
              const keys = !p.user ? '' : t ? formatKeys(t.keys) : formatKeys(defaultKeys(phoneModel(p.model)));
              return { op: 'add', phone: p, keys };
            })
          ]);
          setAdding(false);
        }}
      />

      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={staged.map((s) =>
          s.op === 'add'
            ? {
                key: `add-${s.phone.mac}`,
                description: `Add ${modelLabel(s.phone.model)} ${formatMac(s.phone.mac)}${s.phone.user ? ` for ${personName(dir.data, s.phone.user)}` : ' as a spare'}`,
                intent: {
                  op: 'account.create',
                  kind: 'phone',
                  extension: s.phone.mac,
                  values: { model: s.phone.model, ...(s.phone.user ? { user: s.phone.user } : {}), ...(s.keys ? { keys: s.keys } : {}) }
                } as Intent
              }
            : { key: `remove-${s.mac}`, description: `Remove ${s.label}`, intent: { op: 'account.delete', kind: 'phone', extension: s.mac } as Intent }
        )}
        onUnstage={(key) => setStaged((cur) => cur.filter((s) => (s.op === 'add' ? `add-${s.phone.mac}` : `remove-${s.mac}`) !== key))}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setStaged([]);
            phones.reload();
            templates.reload();
          }
        }}
      />
    </>
  );
}

/** Several phones at once: MAC address, model, whose it is, and which keys to start with. */
function AddPhones({
  open,
  dir,
  templates,
  taken,
  onClose,
  onAdd
}: {
  open: boolean;
  dir: Directory | undefined;
  templates: PhoneTemplate[];
  taken: (mac: string) => boolean;
  onClose: () => void;
  onAdd: (rows: NewPhone[]) => void;
}) {
  const blank = (): NewPhone => ({ mac: '', model: 'yealink-t54w', user: '', template: '' });
  const [rows, setRows] = useState<NewPhone[]>([blank()]);
  const [tried, setTried] = useState(false);
  const people = peopleOnly(dir);
  const set = (i: number, patch: Partial<NewPhone>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const problems = rows.map((r, i) => {
    const mac = normalizeMac(r.mac);
    if (!mac) return 'Enter the 12-character MAC address from the label under the phone.';
    if (taken(mac) || rows.some((o, j) => j < i && normalizeMac(o.mac) === mac)) return 'That phone is already here.';
    const t = templates.find((x) => x.id === r.template);
    if (t && !r.user) return 'Choose whose phone it is to use a template.';
    if (t && t.model !== r.model) return `That template is for a ${modelLabel(t.model)}.`;
    return '';
  });
  const ok = problems.every((p) => !p);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add phones"
      subtitle="The phone gets its settings the next time it starts up."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setTried(true);
              if (ok) onAdd(rows.map((r) => ({ ...r, mac: normalizeMac(r.mac)! })));
            }}
          >
            Add {plural(rows.length, 'phone')}
          </Button>
        </>
      }
    >
      <div className="add-phones">
        {rows.map((r, i) => (
          <fieldset key={i} className="add-phone">
            <legend className="sr-only">Phone {i + 1}</legend>
            <Field
              label="MAC address"
              value={r.mac}
              placeholder="80:5E:C0:12:34:56"
              className="input mono"
              autoCapitalize="characters"
              spellCheck={false}
              onChange={(e) => set(i, { mac: e.target.value })}
              hint={(() => {
                const mac = normalizeMac(r.mac);
                // Not Yealink's own prefix: worth checking the model.
                return mac && !looksYealink(mac) && phoneModel(r.model)?.vendor === 'Yealink' ? 'This doesn’t look like a Yealink MAC address. Check the model.' : undefined;
              })()}
              error={tried ? problems[i] || undefined : undefined}
              data-autofocus={i === rows.length - 1 ? true : undefined}
            />
            <div className="field">
              <label htmlFor={`model-${i}`}>Model</label>
              <select id={`model-${i}`} className="input" value={r.model} onChange={(e) => set(i, { model: e.target.value, template: '' })}>
                {PHONE_MODELS.filter((m) => m.lineKeys > 0 || m.family === 'conference').map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.vendor} {m.model}
                    {m.endOfSale ? ' (no longer sold)' : ''}
                  </option>
                ))}
              </select>
              <span className="hint">{describeModel(phoneModel(r.model)!)}</span>
            </div>
            <DestinationPicker label="Whose phone" mode="people" value={r.user} onChange={(v) => set(i, { user: v })} dir={people} help="Leave empty for a spare." />
            <div className="field">
              <label htmlFor={`tpl-${i}`}>Keys</label>
              <select id={`tpl-${i}`} className="input" value={r.template} onChange={(e) => set(i, { template: e.target.value })} disabled={!r.user}>
                <option value="">Just their line</option>
                {templates
                  .filter((t) => t.model === r.model)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.shared ? ' (Stitch)' : ''}
                    </option>
                  ))}
              </select>
            </div>
            {rows.length > 1 && (
              <button
                type="button"
                className="icon-btn add-phone-remove"
                aria-label={`Remove phone ${i + 1} from the list`}
                onClick={() => setRows((x) => x.filter((_, j) => j !== i))}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </fieldset>
        ))}
        {rows.length < 20 && (
          <Button size="small" variant="ghost" icon="plus" onClick={() => setRows((x) => [...x, { ...blank(), model: x[x.length - 1]?.model ?? 'yealink-t54w' }])}>
            Another phone
          </Button>
        )}
      </div>
    </Dialog>
  );
}

// ---- One phone: settings and keys ---------------------------------------------------------------

function modulesIn(keys: readonly PhoneKey[]): number {
  return keys.reduce((m, k) => Math.max(m, Number(/^E(\d)-/.exec(k.key)?.[1] ?? 0)), 0);
}

/** Keys as a map edit: set one key (or clear it back to the phone's default). */
export function withKey(keys: readonly PhoneKey[], key: string, next: PhoneKey | undefined): PhoneKey[] {
  const rest = keys.filter((k) => k.key !== key);
  return (next ? [...rest, next] : rest).sort((a, b) => keyOrder(a.key) - keyOrder(b.key));
}

/** The keys editor shared by phones and templates: the phone, plus the selected key's settings. */
export function KeysWorkbench({
  model,
  keys,
  setKeys,
  dir,
  owner,
  readOnly,
  changed,
  side,
  listed
}: {
  model: PhoneModel;
  keys: PhoneKey[];
  setKeys: (k: PhoneKey[]) => void;
  /** Every key the phone system lists for this phone, when read: the keys it really has. */
  listed?: readonly string[];
  dir: Directory | undefined;
  owner?: string;
  readOnly?: boolean;
  changed?: ReadonlySet<string>;
  /** Shown beside the phone when no key is selected. */
  side?: ReactNode;
}) {
  const [selected, setSelected] = useState<string>();
  const [modules, setModules] = useState(() => modulesIn(keys));
  useEffect(() => setModules((m) => Math.max(m, modulesIn(keys))), [keys]);
  const nameOf = namer(dir);
  const set = keys.filter((k) => k.type !== 'other' || k.raw);
  // Every key the phone has is drawn, a page at a time (a T87W: 84 keys, 11 a page).
  const count = lineKeyCount(model, keys, listed);
  return (
    <div className="workbench">
      <div className="workbench-phone">
        <PhoneLayout
          model={model}
          keys={keys}
          nameOf={nameOf}
          selected={selected}
          onSelect={readOnly ? undefined : (k) => setSelected((s) => (s === k ? undefined : k))}
          modules={modules}
          owner={owner}
          editing={!readOnly}
          changed={changed}
          lineKeys={count}
        />
        {!readOnly && model.expansion && (
          <div className="workbench-modules">
            {modules < model.expansion.max && (
              <Button size="small" variant="ghost" icon="plus" onClick={() => setModules((m) => m + 1)}>
                Add an {model.expansion.name} expansion module
              </Button>
            )}
            {modules > 0 && modulesIn(keys) < modules && (
              <Button size="small" variant="ghost" onClick={() => setModules((m) => Math.max(modulesIn(keys), m - 1))}>
                Hide the last module
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="workbench-side">
        {selected && !readOnly ? (
          <Card>
            <div className="card-body">
              <KeyEditor
                key={selected}
                model={model}
                keyId={selected}
                value={keys.find((k) => k.key === selected)}
                onChange={(k) => setKeys(withKey(keys, selected, k))}
                dir={dir}
                onClose={() => setSelected(undefined)}
                lineKeys={count}
              />
            </div>
          </Card>
        ) : (
          <>
            {side}
            <Card title={`Keys set (${set.length} of ${count})`}>
              {set.length === 0 ? (
                <p className="muted card-body">{readOnly ? 'Every key does what the phone does by default.' : 'Click a key on the phone to choose what it does.'}</p>
              ) : (
                <ul className="key-list card-body">
                  {set.map((k) => (
                    <li key={k.key}>
                      <button type="button" className={`key-list-row${changed?.has(k.key) ? ' changed' : ''}`} onClick={() => !readOnly && setSelected(k.key)} disabled={readOnly}>
                        <span className="mono muted">{keyName(k.key).replace('Expansion ', 'Exp ')}</span>
                        <span>{keyText(k, nameOf) || '–'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export function PhoneEditor() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { mac = '' } = useParams();
  const navigate = useNavigate();
  const view = useData(() => api.accounts(d, 'phone'), [d]);
  const phone = useData(() => api.account(d, 'phone', mac), [d, mac]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const templates = useData(() => api.templates(d).catch(() => ({ templates: [] as PhoneTemplate[], uses: [] as TemplateUse[] })), [d]);
  const original = phone.data?.account;
  const [keys, setKeys] = useState<PhoneKey[] | null>(null);
  const [owner, setOwner] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [saveAs, setSaveAs] = useState(false);

  const origKeys = useMemo(() => parseKeys(original?.values.keys ?? ''), [original]);
  const k = keys ?? origKeys;
  const user = owner ?? original?.values.user ?? '';
  const modelId = model ?? original?.values.model ?? '';
  const m = phoneModel(modelId);
  const writable = Boolean(view.data?.writable);
  // Keys have a call of their own: they can change even while the phone's other settings can't.
  const keysWritable = Boolean(view.data?.writableFields.includes('keys'));
  const changedKeys = useMemo(() => {
    const before = new Map(origKeys.map((x) => [x.key, JSON.stringify(x)]));
    const after = new Map(k.map((x) => [x.key, JSON.stringify(x)]));
    return new Set([...before.keys(), ...after.keys()].filter((key) => before.get(key) !== after.get(key)));
  }, [origKeys, k]);
  const keysChanged = changedKeys.size > 0;
  const ownerChanged = owner !== null && owner !== (original?.values.user ?? '');
  const modelChanged = model !== null && model !== original?.values.model;
  const dirty = keysChanged || ownerChanged || modelChanged;
  const use = templates.data?.uses.find((u) => u.phone === mac);

  if (phone.error) {
    return (
      <div className="content">
        <Card>
          <LoadError error={phone.error} retry={phone.reload} />
        </Card>
      </div>
    );
  }
  if (!original) return <Loading label="Loading the phone" />;

  const set: Record<string, string> = {};
  if (modelChanged) set.model = modelId;
  if (ownerChanged) set.user = user;
  // Only the keys changed here are sent (and any put back to the default, by name): keys the
  // phone has that this page didn't change stay exactly as the phone system has them.
  // A phone that changes hands keeps its keys: they move to the new person's line.
  if (keysChanged) set.keys = user ? keysChange(origKeys, k) : '';
  else if (ownerChanged && user && k.length) set.keys = formatKeys(k);
  const lines = [
    modelChanged && `model → ${modelLabel(modelId)}`,
    ownerChanged && (user ? `give it to ${personName(dir.data, user)}` : 'make it a spare'),
    keysChanged && plural(changedKeys.size, 'key')
  ].filter(Boolean);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`/t/${encodeURIComponent(d)}/phones`}>Desk phones</Link> · <span className="mono">{formatMac(mac)}</span>
          </>
        }
        title={`${modelLabel(modelId)}${user ? ` · ${personName(dir.data, user)}` : ' · Spare'}`}
        chips={<OnlineChip a={original} />}
        actions={
          <>
            {m && (
              <Button icon="layers" onClick={() => setSaveAs(true)} disabled={k.length === 0}>
                Save as template
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!dirty}
              onClick={() => {
                setReviewKey((x) => x + 1);
                setReviewing(true);
              }}
            >
              Review changes
            </Button>
          </>
        }
      />
      <div className="content">
        {!keysWritable && (
          <Notice tone="warn" icon="lock" title="View only for now">
            {view.data?.notice ?? 'Changing keys here isn’t switched on yet for this phone system.'}
          </Notice>
        )}
        {use && !use.current && (
          <Notice tone="warn" title="Its template has changed">
            This phone was set up from {templates.data?.templates.find((t) => t.id === use.templateId)?.name ?? 'a template'}, which has been changed since.{' '}
            <Link to={`/t/${encodeURIComponent(d)}/templates/${use.templateId}`}>Open the template</Link> to update the phones that use it.
          </Notice>
        )}
        {m ? (
          <KeysWorkbench
            model={m}
            keys={k}
            setKeys={setKeys}
            dir={dir.data}
            owner={user ? `${user}  ${personName(dir.data, user)}` : 'Spare'}
            readOnly={!keysWritable || !user}
            changed={changedKeys}
            {...(original.live?.phone?.slots ? { listed: original.live.phone.slots } : {})}
            side={
              <Card title="This phone">
                <div className="stack card-body">
                  <DestinationPicker
                    label="Whose phone"
                    mode="people"
                    value={user}
                    onChange={(v) => setOwner(v)}
                    dir={peopleOnly(dir.data)}
                    help={user ? 'Their line and these keys go with the phone.' : 'Choose someone to give it a line and keys.'}
                  />
                  <div className="field">
                    <label htmlFor="phone-model">Model</label>
                    <select id="phone-model" className="input" value={modelId} onChange={(e) => setModel(e.target.value)} disabled={!writable}>
                      {!phoneModel(modelId) && <option value={modelId}>{modelLabel(modelId)}</option>}
                      {PHONE_MODELS.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.vendor} {x.model}
                        </option>
                      ))}
                    </select>
                  </div>
                  <dl className="facts">
                    <dt>MAC address</dt>
                    <dd className="mono">{formatMac(mac)}</dd>
                    {original.live?.phone?.firmware && (
                      <>
                        <dt>Firmware</dt>
                        <dd className="mono">{original.live.phone.firmware}</dd>
                      </>
                    )}
                    {original.live?.phone?.userAgent && (
                      <>
                        <dt>Signed in as</dt>
                        <dd>{original.live.phone.userAgent}</dd>
                      </>
                    )}
                  </dl>
                  {templates.data && templates.data.templates.some((t) => t.model === modelId) && keysWritable && user && (
                    <div className="field">
                      <label htmlFor="start-from">Start from a template</label>
                      <select
                        id="start-from"
                        className="input"
                        value=""
                        onChange={(e) => {
                          const t = templates.data?.templates.find((x) => x.id === e.target.value);
                          if (t) setKeys(parseKeys(formatKeys(t.keys)));
                        }}
                      >
                        <option value="">Choose a template…</option>
                        {templates.data.templates
                          .filter((t) => t.model === modelId)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                      </select>
                      <span className="hint">Replaces the keys here; nothing changes until you review and apply.</span>
                    </div>
                  )}
                  {dirty && (
                    <Button
                      size="small"
                      variant="ghost"
                      icon="undo"
                      onClick={() => {
                        setKeys(null);
                        setOwner(null);
                        setModel(null);
                      }}
                    >
                      Undo my edits
                    </Button>
                  )}
                </div>
              </Card>
            }
          />
        ) : (
          <Notice title="Stitch can’t draw this model yet">{modelLabel(modelId)} isn’t in Stitch’s phone list, so its keys can only be changed in the phone system for now.</Notice>
        )}
      </div>

      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={
          dirty
            ? [
                {
                  key: 'phone',
                  description: `${modelLabel(original.values.model)} ${formatMac(mac)}: ${lines.join(', ')}`,
                  intent: { op: 'account.update', kind: 'phone', extension: mac, set } as Intent
                }
              ]
            : []
        }
        onUnstage={() => {
          setKeys(null);
          setOwner(null);
          setModel(null);
        }}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setKeys(null);
            setOwner(null);
            setModel(null);
            phone.reload();
            templates.reload();
          }
        }}
      />

      <SaveAsTemplate
        open={saveAs}
        onClose={() => setSaveAs(false)}
        onSave={async (name) => {
          const { template } = await api.createTemplate(d, { name, model: modelId, keys: formatKeys(k) });
          setSaveAs(false);
          navigate(`/t/${encodeURIComponent(d)}/templates/${template.id}`);
        }}
      />
    </>
  );
}

function SaveAsTemplate({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (name: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Save these keys as a template"
      subtitle="Then put the same keys on other phones in one go."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onSave(name.trim());
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Save template
          </Button>
        </>
      }
    >
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Front desk, Sales team…" maxLength={60} data-autofocus />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

export { modulesIn };
